import React, { useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Modal,
  ModalHeader,
  Row,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Axios from "../../lib/Axios";
import ModalKeyValueViewer from "./helper/ModalKeyValueViewer";
import ModalKeyValueEditor from "./helper/ModalKeyValueEditor";

const normalizeResponse = (payload) => {
  const entries = Object.entries(payload || {});
  return entries
    .filter(([key]) => key && key !== "key")
    .map(([key, value]) => ({
      keyName: key,
      value,
    }));
};

const getValuePreview = (value) => {
  const serialized = JSON.stringify(value);
  if (!serialized) return "-";
  return serialized.length > 80
    ? `${serialized.slice(0, 77)}...`
    : serialized;
};

const ModalKeyValuePage = () => {
  const [keyValues, setKeyValues] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [selectedPair, setSelectedPair] = useState(null);
  const [editorMode, setEditorMode] = useState("create"); // create | edit

  const fetchKeyValues = async () => {
    try {
      const response = await Axios.post("/cms/cms-get/all-modal-keys");
      const result = response?.data?.result;
      const formatted = normalizeResponse(result);
      setKeyValues(formatted);
    } catch (error) {
      console.error("Error fetching modal key value list:", error);
    }
  };

  useEffect(() => {
    fetchKeyValues();
  }, []);

  const handleView = useCallback((row) => {
    setSelectedPair(row);
    setViewModalOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedPair(row);
    setEditorMode("edit");
    setEditorModalOpen(true);
  }, []);

  const handleAddNew = useCallback(() => {
    setSelectedPair(null);
    setEditorMode("create");
    setEditorModalOpen(true);
  }, []);

  const toggleViewModal = () => setViewModalOpen((prev) => !prev);
  const toggleEditorModal = () => setEditorModalOpen((prev) => !prev);

  const columns = useMemo(
    () => [
      {
        name: <span className="font-weight-bold fs-13">Key</span>,
        selector: (row) => row.keyName,
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">Value preview</span>,
        selector: (row) => getValuePreview(row?.value),
        grow: 2,
      },
      {
        name: <span className="font-weight-bold fs-13">Actions</span>,
        cell: (row) => (
          <div className="d-flex gap-2">
            <Button
              size="sm"
              color="info"
              onClick={() => handleView(row)}
              className="edit-item-btn"
            >
              View
            </Button>
            <Button
              size="sm"
              color="success"
              onClick={() => handleEdit(row)}
              className="edit-item-btn"
            >
              Edit
            </Button>
          </div>
        ),
      },
    ],
    [handleView, handleEdit]
  );

  const filteredData = useMemo(() => {
    if (!Array.isArray(keyValues)) return [];
    if (!searchTerm) return keyValues;
    return keyValues.filter((pair) => {
      const keyMatch = pair?.keyName
        ?.toLowerCase()
        ?.includes(searchTerm.toLowerCase());
      const valueMatch = JSON.stringify(pair?.value)
        ?.toLowerCase()
        ?.includes(searchTerm.toLowerCase());
      return keyMatch || valueMatch;
    });
  }, [keyValues, searchTerm]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Modal Key Value" />
          <Col lg={12}>
            <Card>
              <CardHeader>
                <div className="d-flex flex-wrap justify-content-between align-items-center">
                  <h4 className="card-title mb-0">Manage Modal Content</h4>
                  <Button color="success" onClick={handleAddNew}>
                    <i className="ri-add-line align-bottom me-1"></i> Add Key
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <Row className="g-4 mb-3">
                  <Col sm="auto">
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted">Search:</span>
                      <input
                        type="text"
                        className="form-control search"
                        placeholder="Search key or value..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </Col>
                </Row>
                <DataTable columns={columns} data={filteredData} pagination />
              </CardBody>
            </Card>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">Available Keys</h4>
              </CardHeader>
              <CardBody>
                <div className="d-flex flex-wrap gap-2">
                  {keyValues.length === 0 && (
                    <span className="text-muted">No keys found</span>
                  )}
                  {keyValues.map((pair) => (
                    <span key={pair.keyName} className="badge bg-secondary">
                      {pair.keyName}
                    </span>
                  ))}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Container>
      </div>

      <Modal isOpen={viewModalOpen} toggle={toggleViewModal} centered size="lg">
        <ModalHeader className="bg-light p-3" toggle={toggleViewModal}>
          View Modal Value
        </ModalHeader>
        <ModalKeyValueViewer data={selectedPair} toggle={toggleViewModal} />
      </Modal>

      <Modal isOpen={editorModalOpen} toggle={toggleEditorModal} centered size="lg">
        <ModalHeader className="bg-light p-3" toggle={toggleEditorModal}>
          {editorMode === "create" ? "Add" : "Edit"} Modal Key Value
        </ModalHeader>
        <ModalKeyValueEditor
          mode={editorMode}
          data={selectedPair}
          toggle={toggleEditorModal}
          refetch={fetchKeyValues}
        />
      </Modal>
    </React.Fragment>
  );
};

export default ModalKeyValuePage;

